import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Users, AlertCircle, Home, LogOut, MessageSquare, Network, Ban } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useAdmin } from '../contexts/AdminContext';
import { Button } from '@/components/ui/button';

const AdminSidebar: React.FC = () => {
  const location = useLocation();
  const { signOut } = useAuth();
  const { adminUser } = useAdmin();

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const navItems = [
    {
      name: 'Dashboard',
      path: '/admin',
      icon: <Home className="h-5 w-5" />,
    },
    {
      name: 'Users',
      path: '/admin/users',
      icon: <Users className="h-5 w-5" />,
    },
    {
      name: 'Reports',
      path: '/admin/reports',
      icon: <AlertCircle className="h-5 w-5" />,
    },
    {
      name: 'Word Analytics',
      path: '/admin/words',
      icon: <MessageSquare className="h-5 w-5" />,
    },
    {
      name: 'Concept Map',
      path: '/admin/concept-map',
      icon: <Network className="h-5 w-5" />,
    },
    {
      name: 'Banned Words',
      path: '/admin/banned-words',
      icon: <Ban className="h-5 w-5" />,
    },
  ];

  return (
    <div className="h-screen w-64 bg-card border-r border-border flex flex-col">
      <div className="p-4 border-b border-border">
        <div className="flex items-center gap-2">
          <img
            src="/images/elements.png"
            alt="VibeCraft Admin"
            className="w-8 h-8 object-contain"
          />
          <h1 className="text-xl font-bold">VibeCraft Admin</h1>
        </div>
      </div>

      <div className="flex-1 overflow-auto py-4">
        <nav className="space-y-1 px-2">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={cn(
                'flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors',
                isActive(item.path)
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              )}
            >
              {item.icon}
              {item.name}
            </Link>
          ))}
        </nav>
      </div>

      <div className="p-4 border-t border-border">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
              {adminUser?.displayName?.charAt(0) || 'A'}
            </div>
            <div className="flex flex-col">
              <span className="text-sm font-medium">{adminUser?.displayName || 'Admin'}</span>
              <span className="text-xs text-muted-foreground">{adminUser?.email || ''}</span>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={signOut} className="w-full">
            <LogOut className="h-4 w-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminSidebar;
